import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SuperElement } from '../../internals/super/SuperElement';
import { inputStyle } from './Input.styles';
import { event, type EventDispatcher } from '../../utils/event';
import { type Icons } from '../icon';
import '../icon';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { submit } from '../../utils/form-helpers';

export type InputSize = 'small' | 'medium' | 'large';
export type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';
export type InputIconPosition = 'left' | 'right';

@customElement('lr-input')
export class Input extends SuperElement {
    static formAssociated = true;

    @property({ type: String }) label?: string;
    @property({ type: String }) placeholder?: string;
    @property({ type: String }) value = '';
    @property({ type: String }) type: InputType = 'text';
    @property({ type: String, reflect: true }) size: InputSize = 'small';
    @property({ type: Boolean, reflect: true }) disabled = false;
    @property({ type: Boolean }) required = false;
    @property({ type: String }) name?: string;
    @property({ type: String }) icon?: Icons;
    @property({ type: String, reflect: true }) iconPosition?: InputIconPosition;

    @state() private focused = false;

    private internals: ElementInternals;

    @event('on-input')
    private onInput!: EventDispatcher<string>;

    @event('on-change')
    private onChange!: EventDispatcher<string>;

    static styles = [SuperElement.styles, inputStyle];

    constructor() {
        super();
        this.internals = this.attachInternals();
    }

    private handleInput(e: Event) {
        const target = e.target as HTMLInputElement;
        this.value = target.value;
        this.internals.setFormValue(this.value);
        this.onInput(this.value);
    }

    private handleKeyDown(e: KeyboardEvent) {
        if (e.code === 'Enter') {
            const { form } = this.internals;
            if (form) submit(form);
        }
    }

    private handleChange(e: Event) {
        const target = e.target as HTMLInputElement;
        this.value = target.value;
        this.onChange(this.value);
    }

    private handleFocus() {
        this.focused = true;
    }

    private handleBlur() {
        this.focused = false;
    }

    renderElement() {
        const classes = classMap({
            'input-wrapper': true,
            'has-icon': !!this.icon,
            [`icon-${this.iconPosition}`]: !!this.icon
        });
        const iconTag = unsafeStatic(`${this.icon}`);
        const iconComponent = this.icon ? staticHtml`<${iconTag} class='icon' size=${this.size}></${iconTag}>` : null;
        return html`
            <div class=${classes}>
                ${this.label ? html`<label for="input">${this.label}</label>` : nothing}
                ${this.iconPosition === 'left' ? iconComponent : nothing}
                <input
                    id="input"
                    type=${this.type}
                    .value=${this.value}
                    placeholder=${ifDefined(this.placeholder)}
                    ?disabled=${this.disabled}
                    ?required=${this.required}
                    name=${ifDefined(this.name)}
                    @input=${this.handleInput}
                    @change=${this.handleChange}
                    @focus=${this.handleFocus}
                    @blur=${this.handleBlur}
                    @keydown=${this.handleKeyDown}
                />
                ${this.iconPosition === 'right' ? iconComponent : nothing}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'lr-input': Input;
    }
}
